import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  getUnassignedSubmissions,
  getAvailableReviewers,
  createAssignment,
  listAssignments,
  reassignAssignment,
  type UnassignedSubmission,
  type AvailableReviewer,
  type AssignmentRecord,
} from '../api/admin';
import type { User } from '../App';
import { Loader2, UserPlus, Users, FileText, ArrowLeft, Eye } from 'lucide-react';

interface AdminAssignmentsDashboardProps {
  user: User;
  onBack: () => void;
  onReviewClick?: (submissionId: string) => void;
}

export function AdminAssignmentsDashboard({ user, onBack, onReviewClick }: AdminAssignmentsDashboardProps) {
  const [unassigned, setUnassigned] = useState<UnassignedSubmission[]>([]);
  const [unassignedPagination, setUnassignedPagination] = useState({ total: 0, page: 1, limit: 20, total_pages: 0 });
  const [assignments, setAssignments] = useState<AssignmentRecord[]>([]);
  const [summary, setSummary] = useState({ pending: 0, in_progress: 0, completed: 0, overdue: 0 });
  const [reviewers, setReviewers] = useState<AvailableReviewer[]>([]);
  const [loadingUnassigned, setLoadingUnassigned] = useState(true);
  const [loadingAssignments, setLoadingAssignments] = useState(true);
  const [loadingReviewers, setLoadingReviewers] = useState(false);
  const [reviewerMatchMode, setReviewerMatchMode] = useState<'tech' | 'all'>('all');
  const [error, setError] = useState<string | null>(null);

  const [assignModalSubmission, setAssignModalSubmission] = useState<UnassignedSubmission | null>(null);
  const [assignPriority, setAssignPriority] = useState('normal');
  const [assignDueDate, setAssignDueDate] = useState('');
  const [assignNotes, setAssignNotes] = useState('');
  const [assigningReviewerId, setAssigningReviewerId] = useState<string | null>(null);
  const [reassigningId, setReassigningId] = useState<string | null>(null);

  const loadUnassigned = useCallback(async () => {
    setLoadingUnassigned(true);
    setError(null);
    try {
      const res = await getUnassignedSubmissions({ status: 'unassigned', page: 1, limit: 50 });
      setUnassigned(res.submissions);
      setUnassignedPagination(res.pagination);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load unassigned');
    } finally {
      setLoadingUnassigned(false);
    }
  }, []);

  const loadAssignments = useCallback(async () => {
    setLoadingAssignments(true);
    try {
      const res = await listAssignments({ page: 1, limit: 50 });
      setAssignments(res.assignments);
      setSummary(res.summary);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load assignments');
    } finally {
      setLoadingAssignments(false);
    }
  }, []);

  const loadReviewers = useCallback(async (techArea?: string) => {
    setLoadingReviewers(true);
    try {
      setReviewerMatchMode(techArea ? 'tech' : 'all');
      const exactMatch = await getAvailableReviewers({
        expertise: techArea || undefined,
        availability_status: 'available',
      });

      // If no exact expertise matches are found, fall back to all available reviewers
      // so newly uploaded/incomplete reviewer profiles are still assignable.
      if (techArea && exactMatch.reviewers.length === 0) {
        const fallback = await getAvailableReviewers({
          availability_status: 'available',
        });
        setReviewers(fallback.reviewers);
        setReviewerMatchMode('all');
      } else {
        setReviewers(exactMatch.reviewers);
      }
    } catch (e: unknown) {
      setReviewers([]);
      setReviewerMatchMode('all');
    } finally {
      setLoadingReviewers(false);
    }
  }, []);

  useEffect(() => {
    loadUnassigned();
    loadAssignments();
  }, [loadUnassigned, loadAssignments]);

  useEffect(() => {
    if (assignModalSubmission) {
      loadReviewers(assignModalSubmission.technology_area || undefined);
    }
  }, [assignModalSubmission, loadReviewers]);

  const openAssignModal = (sub: UnassignedSubmission) => {
    setAssignModalSubmission(sub);
    setAssignPriority('normal');
    setAssignDueDate('');
    setAssignNotes('');
  };

  const handleAssign = async (reviewerUuid: string) => {
    if (!assignModalSubmission) return;
    setAssigningReviewerId(reviewerUuid);
    try {
      await createAssignment({
        submission_uuid: assignModalSubmission.submission_uuid,
        reviewer_uuid: reviewerUuid,
        priority: assignPriority,
        due_date: assignDueDate || undefined,
        admin_notes: assignNotes || undefined,
      });
      setAssignModalSubmission(null);
      loadUnassigned();
      loadAssignments();
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Failed to create assignment');
    } finally {
      setAssigningReviewerId(null);
    }
  };

  const handleReassign = async (assignmentUuid: string, newReviewerUuid: string) => {
    setReassigningId(assignmentUuid);
    try {
      await reassignAssignment(assignmentUuid, {
        new_reviewer_uuid: newReviewerUuid,
        reassignment_reason: 'Reassigned by admin',
      });
      loadAssignments();
      loadUnassigned();
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Failed to reassign');
    } finally {
      setReassigningId(null);
    }
  };

  const formatDate = (d?: string | null) => (d ? new Date(d).toLocaleDateString() : '—');

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" onClick={onBack}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to dashboard
          </Button>
          <h1 className="text-[var(--navy)] font-display font-semibold">Assignment management</h1>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">{error}</div>
      )}

      {/* Section 1: Unassigned queue */}
      <Card>
        <CardHeader>
          <CardTitle className="text-[var(--navy)] font-display flex items-center gap-2">
            <FileText className="w-5 h-5 text-[var(--gold)]" />
            Unassigned submissions
          </CardTitle>
          <CardDescription>Submitted innovations waiting to be assigned to a reviewer</CardDescription>
        </CardHeader>
        <CardContent>
          {loadingUnassigned ? (
            <div className="flex justify-center py-8"><Loader2 className="w-8 h-8 animate-spin text-[var(--gold)]" /></div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Tech area</TableHead>
                  <TableHead>Submitted by</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {unassigned.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-[var(--navy)]/70 py-8">
                      No unassigned submissions.
                    </TableCell>
                  </TableRow>
                ) : (
                  unassigned.map((s) => (
                    <TableRow key={s.submission_uuid}>
                      <TableCell>
                        <div className="font-medium">{s.title}</div>
                        <div className="text-sm text-slate-500 truncate max-w-xs">{s.abstract?.slice(0, 80)}…</div>
                      </TableCell>
                      <TableCell>{s.technology_area || '—'}</TableCell>
                      <TableCell>{s.submitter_email || s.submitter_uuid}</TableCell>
                      <TableCell>{formatDate(s.submission_date)}</TableCell>
                      <TableCell>
                        <Button size="sm" onClick={() => openAssignModal(s)}>
                          <UserPlus className="w-4 h-4 mr-1" />
                          Assign reviewer
                        </Button>
                        {onReviewClick && (
                          <Button variant="outline" size="sm" className="ml-2" onClick={() => onReviewClick(s.submission_uuid)}>
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Section 2: Active assignments */}
      <Card>
        <CardHeader>
          <CardTitle className="text-[var(--navy)] font-display flex items-center gap-2">
            <Users className="w-5 h-5 text-[var(--gold)]" />
            Active assignments
          </CardTitle>
          <CardDescription>Summary: Pending {summary.pending} · In progress {summary.in_progress} · Completed {summary.completed} · Overdue {summary.overdue}</CardDescription>
        </CardHeader>
        <CardContent>
          {loadingAssignments ? (
            <div className="flex justify-center py-8"><Loader2 className="w-8 h-8 animate-spin text-[var(--gold)]" /></div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Submission</TableHead>
                  <TableHead>Assigned to</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Due date</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {assignments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-[var(--navy)]/70 py-8">
                      No assignments yet.
                    </TableCell>
                  </TableRow>
                ) : (
                  assignments.map((a) => (
                    <TableRow key={a.assignment_uuid}>
                      <TableCell>
                        <div className="font-medium">{a.submission.title}</div>
                        <div className="text-sm text-slate-500">{a.submission.technology_area || '—'}</div>
                      </TableCell>
                      <TableCell>{a.reviewer.name}</TableCell>
                      <TableCell>
                        <Badge variant={a.assignment_status === 'completed' ? 'default' : 'outline'}>
                          {a.assignment_status}
                        </Badge>
                        {a.is_overdue && <Badge variant="destructive" className="ml-1">Overdue</Badge>}
                      </TableCell>
                      <TableCell>{formatDate(a.due_date)} {a.days_remaining != null && a.assignment_status !== 'completed' && `(${a.days_remaining}d)`}</TableCell>
                      <TableCell>
                        {onReviewClick && (
                          <Button variant="outline" size="sm" onClick={() => onReviewClick(a.submission.submission_uuid)}>
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Assignment modal */}
      <Dialog open={!!assignModalSubmission} onOpenChange={(open) => !open && setAssignModalSubmission(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Assign reviewer</DialogTitle>
            <DialogDescription>
              {assignModalSubmission && (
                <>Assign &quot;{assignModalSubmission.title}&quot; to a reviewer. Filter by expertise if needed.</>
              )}
            </DialogDescription>
          </DialogHeader>
          {assignModalSubmission && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-[var(--navy)] mb-2">Submission details</h4>
                <p className="text-sm text-slate-600 mb-1"><strong>Tech area:</strong> {assignModalSubmission.technology_area || '—'}</p>
                <p className="text-sm text-slate-600 mb-1"><strong>Submitted by:</strong> {assignModalSubmission.submitter_email || assignModalSubmission.submitter_uuid}</p>
                <p className="text-sm text-slate-600 mb-2"><strong>Date:</strong> {formatDate(assignModalSubmission.submission_date)}</p>
                <p className="text-sm text-slate-700">{assignModalSubmission.abstract}</p>
                <div className="mt-4 space-y-2">
                  <Label>Priority</Label>
                  <select
                    className="w-full border rounded-md px-3 py-2"
                    value={assignPriority}
                    onChange={(e) => setAssignPriority(e.target.value)}
                  >
                    <option value="low">Low</option>
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                  <Label>Due date (optional)</Label>
                  <Input
                    type="date"
                    value={assignDueDate}
                    onChange={(e) => setAssignDueDate(e.target.value)}
                  />
                  <Label>Admin notes (optional)</Label>
                  <Textarea
                    value={assignNotes}
                    onChange={(e) => setAssignNotes(e.target.value)}
                    rows={2}
                    placeholder="e.g. Prefer IoT expertise"
                  />
                </div>
              </div>
              <div>
                <h4 className="font-medium text-[var(--navy)] mb-2">Available reviewers</h4>
                {reviewerMatchMode === 'all' && assignModalSubmission.technology_area && (
                  <p className="text-xs text-slate-500 mb-2">
                    No exact expertise match found for "{assignModalSubmission.technology_area}". Showing all available reviewers.
                  </p>
                )}
                {loadingReviewers ? (
                  <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>
                ) : reviewers.length === 0 ? (
                  <p className="text-sm text-slate-600">No reviewers with capacity found. Try broadening filters.</p>
                ) : (
                  <ul className="space-y-3 max-h-80 overflow-y-auto">
                    {reviewers.map((r) => (
                      <li key={r.reviewer_uuid} className="border rounded-lg p-3 flex flex-wrap items-center justify-between gap-2">
                        <div>
                          <div className="font-medium">{r.email}</div>
                          <div className="text-xs text-slate-500">
                            Expertise: {(r.profile.primary_expertise || []).join(', ') || '—'} · Slots: {r.capacity.available_slots}/{r.capacity.max_assignments}
                          </div>
                        </div>
                        <Button
                          size="sm"
                          disabled={r.capacity.available_slots <= 0 || assigningReviewerId !== null}
                          onClick={() => handleAssign(r.reviewer_uuid)}
                        >
                          {assigningReviewerId === r.reviewer_uuid ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Assign'}
                        </Button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
